// Die Sensoren (Klima, CO2-Monitor, LiPo-Wächter, Wassermelder und die Klima-
// Übersicht) -- EIN gemeinsamer Stand für climate-devices, co2-summary,
// lipoguard-summary, waterleak-summary und klima-summary.
//
// Datenfluss (seit 02.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Sensoren über das Protokoll“): jedes Gerät der vier MQTT-Caches ist eine
// Instanz sensor.<id>.stand, jeder Sensor der Klima-Übersicht eine Instanz
// klima.<id>.bewertung. Das Backend gleicht die Caches auf Anstoß ab (jede
// Meldung eines Geräts) und rechnet die Übersicht einmal für alle Browser (im
// Sicherheitstakt und wenn sich ein für die Bewertung zählender Zustand ändert); der Hub verwirft Unverändertes und schickt
// Deltas. Bis dahin holte jeder der fünf Stores seine Liste alle 10 bzw. 30 s
// per REST und zusätzlich bei jedem WS-Signal -- je Tab, je Store.
//   - Hier stehen die einzigen Abo-Stellen (`sensor.*.stand`,
//     `klima.*.bewertung`). Aus einem Stand baut lib/sensoren-protokoll.ts die
//     REST-Form seiner Quelle: `GET /v2-api/climate` (nur Geräte mit
//     Temperatur), `/co2monitor`, `/lipoguard`, `/waterleak`; aus einer
//     Bewertung den `KlimaSensor` von `GET /v2-api/klima/summary`. Die Lage der
//     Übersicht (status, ok_sensors, total_sensors) zählt klima-summary aus den
//     Sensoren.
//   - REST (die fünf Endpunkte) nur beim Start, als Revalidator (Reconnect,
//     Tab kommt zurück) und als Neuversuch 15 s nach einem Fehlschlag --
//     jeweils alle fünf --, dazu in refreshSensoren (nach einer Aktion), das
//     nur den Endpunkt EINER Liste holt (die Übersicht ist eine teure
//     Rechnung). KEIN Takt, KEIN Abo
//     auf die alten WS-Kanäle "climate", "co2monitor", "lipoguard" und
//     "waterleak" mehr.
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` hinterlässt einen Grabstein. Jede Liste gilt für sich als bekannt
//     (Ablage, Abruf oder Stand): vorher liefert ihr Getter `undefined`.
//
// Mehrere Stores teilen sich den Stand: startSensoren() zählt mit und liefert
// die Abmeldung; der letzte, der geht, schließt ab.
//
// SWR: ja -- die zuletzt gesehenen Listen kommen beim Start aus der Ablage.

import { getJSON } from "./api";
import { clearPersisted, loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { KlimaBewertung, SensorStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import {
  bewertungIdAusInstanz,
  co2GeraetAusProtokoll,
  klimaGeraetAusProtokoll,
  klimaSchluessel,
  klimaSensorAusProtokoll,
  lipoGeraetAusProtokoll,
  sensorIdAusInstanz,
  vergleicheKlimaSensoren,
  vergleicheNachId,
  vergleicheNachName,
  wasserGeraetAusProtokoll,
} from "./sensoren-protokoll";
import type { ClimateDevice, Co2MonitorDevice, KlimaSensor, LipoguardDevice, WaterleakDevice } from "./types";

const STORAGE_KEY = "sensoren";

// Die Ablage-Schlüssel der fünf alten Stores (bis 02.11.2026): sie werden nicht
// mehr geschrieben; startSensoren räumt sie einmal je Sitzung weg.
const ALTE_SCHLUESSEL = ["climate-devices", "co2", "klima", "lipoguard", "waterleak"];
let _altesGeraeumt = false;

/** Die Ablage: eine Liste fehlt, solange sie unbekannt war. */
interface Gespeichert {
  klima?: ClimateDevice[];
  co2?: Co2MonitorDevice[];
  lipo?: LipoguardDevice[];
  wasser?: WaterleakDevice[];
  bewertung?: KlimaSensor[];
}

const _klima = new ProtokollListe<ClimateDevice>((e) => e.name, vergleicheNachName);
const _co2 = new ProtokollListe<Co2MonitorDevice>((e) => e.id, vergleicheNachId);
const _lipo = new ProtokollListe<LipoguardDevice>((e) => e.id, vergleicheNachId);
const _wasser = new ProtokollListe<WaterleakDevice>((e) => e.name, vergleicheNachName);
const _bewertung = new ProtokollListe<KlimaSensor>((e) => klimaSchluessel(e.domain, e.sensor_id), vergleicheKlimaSensoren);

// Ob die jeweilige Liste etwas weiß (Ablage, Abruf oder Stand): vorher `undefined`.
const _bekannt = $state({ klima: false, co2: false, lipo: false, wasser: false, bewertung: false });

let _nutzer = 0;
let _unsub: Array<() => void> = [];
// Instanzname -> Quelle und Name bzw. Schlüssel: `weg` nennt nur die Instanz.
const _instanzen = new Map<string, { quelle: string; name: string }>();
const _bewertungen = new Map<string, string>();

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (Messwerte ändern sich im Sekundentakt):
// gespeichert wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const g: Gespeichert = {
      klima: _bekannt.klima ? $state.snapshot(_klima.items) : undefined,
      co2: _bekannt.co2 ? $state.snapshot(_co2.items) : undefined,
      lipo: _bekannt.lipo ? $state.snapshot(_lipo.items) : undefined,
      wasser: _bekannt.wasser ? $state.snapshot(_wasser.items) : undefined,
      bewertung: _bekannt.bewertung ? $state.snapshot(_bewertung.items) : undefined,
    };
    persist(STORAGE_KEY, g);
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  if (sensorIdAusInstanz(instanz) === undefined) return;
  const st = skaliere<SensorStand>("sensor.stand", daten);
  const quelle = st.quelle ?? "";
  const name = st.name ?? "";
  let geaendert = false;
  // Vor dem ersten Abruf legt laden() die Geräte über die Antwort; bis dahin
  // wäre die Liste leer, obwohl Stände da sind.
  switch (quelle) {
    case "klima": {
      // Geräte ohne Temperatur führt REST nicht (wie energieGeraetAusProtokoll).
      const g = klimaGeraetAusProtokoll(st);
      geaendert = g ? _klima.stand(g) : _klima.weg(name);
      if (_klima.bereit) _bekannt.klima = true;
      break;
    }
    case "co2":
      geaendert = _co2.stand(co2GeraetAusProtokoll(st)!);
      if (_co2.bereit) _bekannt.co2 = true;
      break;
    case "lipo":
      geaendert = _lipo.stand(lipoGeraetAusProtokoll(st)!);
      if (_lipo.bereit) _bekannt.lipo = true;
      break;
    case "wasser":
      geaendert = _wasser.stand(wasserGeraetAusProtokoll(st)!);
      if (_wasser.bereit) _bekannt.wasser = true;
      break;
    default:
      return;
  }
  _instanzen.set(instanz, { quelle, name });
  if (geaendert) persistSpaeter();
}

function beiWeg(instanz: string): void {
  const i = _instanzen.get(instanz);
  if (!i) return;
  _instanzen.delete(instanz);
  let geaendert = false;
  if (i.quelle === "klima") geaendert = _klima.weg(i.name);
  else if (i.quelle === "co2") geaendert = _co2.weg(i.name);
  else if (i.quelle === "lipo") geaendert = _lipo.weg(i.name);
  else if (i.quelle === "wasser") geaendert = _wasser.weg(i.name);
  if (geaendert) persistSpaeter();
}

function beiBewertung(instanz: string, daten: Record<string, unknown>): void {
  if (bewertungIdAusInstanz(instanz) === undefined) return;
  const b = skaliere<KlimaBewertung>("klima.bewertung", daten);
  const k = klimaSensorAusProtokoll(b);
  _bewertungen.set(instanz, klimaSchluessel(k.domain, k.sensor_id));
  const geaendert = _bewertung.stand(k);
  if (_bewertung.bereit) _bekannt.bewertung = true;
  if (geaendert) persistSpaeter();
}

function beiBewertungWeg(instanz: string): void {
  const k = _bewertungen.get(instanz);
  if (k === undefined) return;
  _bewertungen.delete(instanz);
  if (_bewertung.weg(k)) persistSpaeter();
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage geholtes,
// inzwischen verschwundenes Gerät stehen (für das kommt kein `weg`, diese
// Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

/** Die fünf Listen des gemeinsamen Stands. */
export type SensorArt = "klima" | "co2" | "lipo" | "wasser" | "bewertung";
const ALLE_ARTEN: SensorArt[] = ["klima", "co2", "lipo", "wasser", "bewertung"];

// Je Liste: Endpunkt (liefert die Liste) und ihr Stand.
const _listen = {
  klima: { holen: () => getJSON<ClimateDevice[]>("/v2-api/climate"), liste: _klima },
  co2: { holen: () => getJSON<Co2MonitorDevice[]>("/v2-api/co2monitor"), liste: _co2 },
  lipo: { holen: () => getJSON<LipoguardDevice[]>("/v2-api/lipoguard"), liste: _lipo },
  wasser: { holen: () => getJSON<WaterleakDevice[]>("/v2-api/waterleak"), liste: _wasser },
  // Der teure Endpunkt: eine Rechnung über die Verlaufstabellen.
  bewertung: {
    holen: () => getJSON<{ sensors: KlimaSensor[] | null }>("/v2-api/klima/summary").then((s) => s.sensors ?? []),
    liste: _bewertung,
  },
} as const;

// Eine Liste aus ihrer Antwort mischen; true, wenn sich die Liste geändert hat.
function mische<T extends object>(art: SensorArt, liste: ProtokollListe<T>, r: PromiseSettledResult<T[]>, ab: number): boolean {
  const geaendert = liste.mische(r.status === "fulfilled" ? (r.value ?? []) : undefined, ab);
  if (r.status === "fulfilled" || liste.neuer(ab)) _bekannt[art] = true;
  return geaendert;
}

// Holt die genannten Listen (Standard: alle) und mischt jede für sich.
async function laden(arten: SensorArt[] = ALLE_ARTEN): Promise<void> {
  const ab = arten.map((a) => _listen[a].liste.nr);
  const lauf = _lauf;
  // Die Listen getrennt: scheitert eine, gelten die anderen trotzdem.
  const antworten = await Promise.allSettled(arten.map((a) => _listen[a].holen() as Promise<object[]>));
  if (lauf !== _lauf) return;
  try {
    // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
    // trotzdem -- beim ersten Abruf wären die Listen sonst leer, bis jedes
    // Gerät sich ändert.
    const geaendert = arten.map((a, i) => mische(a, _listen[a].liste as ProtokollListe<object>, antworten[i], ab[i]));
    if (geaendert.some(Boolean)) persistSpaeter();
  } finally {
    // Nur die geholten Listen gelten nun als abgerufen.
    for (const a of arten) _listen[a].liste.bereit = true;
  }
  if (antworten.some((r) => r.status === "rejected") && _nutzer > 0 && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_nutzer > 0) void laden();
    }, NEUVERSUCH_MS);
  }
}

/** Meldet einen Nutzer des gemeinsamen Stands an (der erste startet Abo und
 *  Abruf) und liefert die Abmeldung (der letzte schließt ab). */
export function startSensoren(): () => void {
  _nutzer++;
  if (_nutzer === 1) {
    _lauf++;
    // SWR: gecachte Listen sofort einspielen. Das Protokoll bringt die frischen
    // Werte danach in Sekunden, der Cache deckt nur die First-Paint-Lücke.
    if (!_altesGeraeumt) {
      _altesGeraeumt = true;
      for (const k of ALTE_SCHLUESSEL) clearPersisted(k);
    }
    const cached = loadPersisted<Gespeichert>(STORAGE_KEY);
    if (cached) {
      if (cached.klima) {
        _klima.items = cached.klima;
        _bekannt.klima = true;
      }
      if (cached.co2) {
        _co2.items = cached.co2;
        _bekannt.co2 = true;
      }
      if (cached.lipo) {
        _lipo.items = cached.lipo;
        _bekannt.lipo = true;
      }
      if (cached.wasser) {
        _wasser.items = cached.wasser;
        _bekannt.wasser = true;
      }
      if (cached.bewertung) {
        _bewertung.items = cached.bewertung;
        _bekannt.bewertung = true;
      }
    }
    void laden();
    _unsub.push(abonniere("sensor.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg }));
    _unsub.push(abonniere("klima.*.bewertung", { rate: "normal" }, { stand: beiBewertung, weg: beiBewertungWeg }));
    // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
    // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
    // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
    _unsub.push(registerRevalidator(() => laden()));
  }
  let aktiv = true;
  return () => {
    if (!aktiv) return;
    aktiv = false;
    _nutzer--;
    if (_nutzer === 0) stoppe();
  };
}

function stoppe(): void {
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _klima.leere();
  _co2.leere();
  _lipo.leere();
  _wasser.leere();
  _bewertung.leere();
  _instanzen.clear();
  _bewertungen.clear();
  _bekannt.klima = false;
  _bekannt.co2 = false;
  _bekannt.lipo = false;
  _bekannt.wasser = false;
  _bekannt.bewertung = false;
}

/** Die Klima-Geräte mit Temperatur (wie `GET /v2-api/climate`); `undefined`,
 *  solange weder Ablage noch Abruf noch Protokoll etwas geliefert haben. */
export function getKlimaGeraete(): ClimateDevice[] | undefined {
  return _bekannt.klima ? _klima.items : undefined;
}

/** Die CO2-Monitore (wie `GET /v2-api/co2monitor`); `undefined`, solange nichts bekannt ist. */
export function getCo2Geraete(): Co2MonitorDevice[] | undefined {
  return _bekannt.co2 ? _co2.items : undefined;
}

/** Die LiPo-Wächter (wie `GET /v2-api/lipoguard`); `undefined`, solange nichts bekannt ist. */
export function getLipoGeraete(): LipoguardDevice[] | undefined {
  return _bekannt.lipo ? _lipo.items : undefined;
}

/** Die Wassermelder (wie `GET /v2-api/waterleak`); `undefined`, solange nichts bekannt ist. */
export function getWasserGeraete(): WaterleakDevice[] | undefined {
  return _bekannt.wasser ? _wasser.items : undefined;
}

/** Die Sensoren der Klima-Übersicht (`sensors` von `GET /v2-api/klima/summary`);
 *  `undefined`, solange nichts bekannt ist. */
export function getKlimaSensoren(): KlimaSensor[] | undefined {
  return _bekannt.bewertung ? _bewertung.items : undefined;
}

/** Holt eine Liste sofort per REST -- nach einer Aktion, deren Ergebnis die
 *  Seite gleich zeigen soll (das Protokoll braucht einen Moment). Nur ihren
 *  Endpunkt: die Übersicht (`bewertung`) ist eine Rechnung über die
 *  Verlaufstabellen und gehört nicht zu jeder Aktion an einem Klimagerät. */
export function refreshSensoren(art: SensorArt): Promise<void> {
  // Ohne Nutzer (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (_nutzer === 0) return Promise.resolve();
  return laden([art]);
}
